import { useState } from "react";
import { CheckCircle, XCircle, Circle, CaretDown, CaretUp, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

/** Step-by-step result of /inbox/smtp/diagnose or /inbox/imap/diagnose — pass/fail per step with the server's exact reply. */
export default function ConnectionTestReport({ report, testid = "connection-report" }) {
  const [open, setOpen] = useState(!report?.ok);
  if (!report) return null;
  const failed = report.steps?.find((s) => !s.ok);
  const copy = () => {
    const lines = report.steps.map((s) => `${s.ok ? "PASS" : "FAIL"}  ${s.label}${s.detail ? ` — ${s.detail}` : ""}`);
    navigator.clipboard?.writeText([`${report.host}:${report.port} (${report.security || (report.ssl ? "ssl" : "plain")})`, ...lines].join("\n")).then(() => toast.success("Report copied"));
  };
  return (
    <div className={`rounded-md border text-xs ${report.ok ? "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20" : "border-red-300 bg-red-50/60 dark:bg-red-950/20"}`} data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-3 py-2 text-left" data-testid={`${testid}-toggle`}>
        {report.ok ? <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0" /> : <XCircle size={16} weight="fill" className="text-red-600 shrink-0" />}
        <span className={`flex-1 font-medium ${report.ok ? "text-emerald-800 dark:text-emerald-300" : "text-red-800 dark:text-red-300"}`} data-testid={`${testid}-summary`}>
          {report.ok ? report.message : `Failed at “${failed?.label}”`}
          <span className="ml-2 font-normal text-muted-foreground">{report.steps.filter((s) => s.ok).length}/{report.steps.length} checks · {report.duration_ms} ms</span>
        </span>
        {open ? <CaretUp size={14} className="text-muted-foreground" /> : <CaretDown size={14} className="text-muted-foreground" />}
      </button>
      {open && (
        <div className="border-t border-border/60 px-3 py-2">
          <ol className="space-y-1.5" data-testid={`${testid}-steps`}>
            {report.steps.map((s) => (
              <li key={s.key} className="flex items-start gap-2" data-testid={`${testid}-step-${s.key}`} data-ok={s.ok ? "1" : "0"}>
                {s.ok ? <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-emerald-600" /> : <XCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-red-600" />}
                <div className="min-w-0 flex-1">
                  <p className={`font-medium ${s.ok ? "" : "text-red-700 dark:text-red-300"}`}>{s.label}{s.ms > 0 && <span className="ml-1 font-normal text-muted-foreground">{s.ms} ms</span>}</p>
                  {s.detail && <p className={`break-words font-mono text-[11px] leading-snug ${s.ok ? "text-muted-foreground" : "text-red-800 dark:text-red-200"}`}>{s.detail}</p>}
                </div>
              </li>
            ))}
            {(report.pending || []).map((p) => (
              <li key={p.key} className="flex items-center gap-2 text-muted-foreground" data-testid={`${testid}-step-${p.key}-skipped`}><Circle size={14} className="shrink-0" /> <span>{p.label} <span className="text-[10px]">— not reached</span></span></li>
            ))}
          </ol>
          {!report.ok && (
            <div className="mt-2 rounded border border-red-200 bg-white/70 p-2 dark:bg-black/20" data-testid={`${testid}-error`}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-300">Exact server reply</p>
              <p className="break-words font-mono text-[11px] text-red-900 dark:text-red-100" data-testid={`${testid}-server-response`}>{report.server_response || "(no reply — the connection was closed)"}</p>
              {report.hint && <p className="mt-1.5 text-[11px] text-slate-700 dark:text-slate-300" data-testid={`${testid}-hint`}>{report.hint}</p>}
            </div>
          )}
          <button type="button" onClick={copy} className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid={`${testid}-copy`}><Copy size={12} /> Copy report for your IT / provider</button>
        </div>
      )}
    </div>
  );
}
